import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { generateAutomationName } from "../../../../../helpers/generateAutomationName";
import { DATA_AUTOMATION_NAMES } from "../constants";
import type { ContentFilterDraft, DomainOption, FilterValidationError } from "../types";

import { FilterCard } from "./FilterCard";
import { FilterCardSkeleton } from "./FilterCardSkeleton";

export type FilterCardListProps = {
    allDomains: string[];
    domainOptions: DomainOption[];
    drafts: ContentFilterDraft[];
    selectedDomains: Set<string>;
    isLoading: boolean;
    validationErrors: Record<string, FilterValidationError>;
    onChangeFilter: (id: string, patch: Partial<ContentFilterDraft>) => void;
    onDeleteFilter: (id: string) => void;
    onAddFilter: () => void;
};

export const FilterCardList = ({
    allDomains,
    domainOptions,
    drafts,
    selectedDomains,
    isLoading,
    validationErrors,
    onChangeFilter,
    onDeleteFilter,
    onAddFilter,
}: FilterCardListProps) => {
    const { translate } = useTranslate();

    const canAddMoreFilters = drafts.length < allDomains.length;

    return (
        <Stack gap="24px">
            <Stack gap="16px">
                {isLoading ? (
                    <FilterCardSkeleton />
                ) : (
                    drafts.map((draft) => {
                        const availableOptions = domainOptions.filter(
                            (option) => option.id === draft.domain || !selectedDomains.has(option.id),
                        );

                        return (
                            <FilterCard
                                key={draft.id}
                                draft={draft}
                                availableOptions={availableOptions}
                                error={validationErrors[draft.id]}
                                onChange={onChangeFilter}
                                onDelete={onDeleteFilter}
                            />
                        );
                    })
                )}
            </Stack>

            {!isLoading && canAddMoreFilters ? (
                <Flex>
                    <IconButton
                        type="flat"
                        iconName="add"
                        onClick={onAddFilter}
                        dataAutomation={generateAutomationName("settings", [...DATA_AUTOMATION_NAMES, "filter", "add"])}
                    >
                        {translate("activation.settings.news.filter.add")}
                    </IconButton>
                </Flex>
            ) : null}
        </Stack>
    );
};
